import { ContainerOutlined } from '@ant-design/icons'
import { Button, Form } from 'antd'
import { t } from 'i18next'
import { ReactNode, useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  ModalBase,
  ModalContentContainer,
  ModalStepsTitle,
} from '../../../components'
import { ModalSteps, optionalRequiredMark } from '../../../constants/constants'
import useWorkspaces from '../../../hooks/useWorkspaces'
import { notificationService, workspaceService } from '../../../services'
import { RootState } from '../../../store'
import { WorkspaceDetails } from '../../../store/reducers/workspaceReducer'
import { routePaths } from '../../RootPage'
import CreateWorkspaceModalFooter from './footers/CreateWorkspaceModalFooter'
import CreateWorkspaceModalOutputStep from './steps/CreateWorkspaceModalDataStoreStep'
import CreateWorkspaceModalDefinitionStep from './steps/CreateWorkspaceModalDefinitionStep'
import CreateWorkspaceModalPrivacyStep from './steps/CreateWorkspaceModalPrivacyStep'
import CreateWorkspaceModalResourcesStep from './steps/CreateWorkspaceModalResourcesStep'
import CreateWorkspaceModalReviewStep from './steps/CreateWorkspaceModalReviewStep'
import CreateWorkspaceModalTypeStep from './steps/CreateWorkspaceModalTypeStep'

interface CreateWorkspaceModalProps {
  disabled: boolean
}

const CreateWorkspaceModal = ({ disabled }: CreateWorkspaceModalProps) => {
  const [form] = Form.useForm()
  const [open, setOpen] = useState(false)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)

  const navigate = useNavigate()

  const { refreshWorkspaceList } = useWorkspaces({
    preventFetch: true,
  })
  const currentUser = useSelector((state: RootState) => state.user.currentUser)
  const defaultValues = useMemo(
    () => ({
      title: '',
      description: '',
      participants: currentUser ? [currentUser.id] : [],
      selectedDataStores: [],
      selectedDataSource: undefined,
      selectedDataStore: undefined,
    }),
    [currentUser]
  )

  const stepItems: ModalSteps[] = [
    {
      title: t('createWorkspaceModal.definitionStep.title'),
    },
    {
      title: t('createWorkspaceModal.typeStep.title'),
    },
    {
      title: t('createWorkspaceModal.resourcesStep.title'),
    },
    {
      title: t('createWorkspaceModal.storeStep.title'),
    },
    {
      title: t('createWorkspaceModal.privacyStep.title'),
    },
    {
      title: t('createWorkspaceModal.reviewStep.title'),
    },
  ]

  const resetValues = useCallback(() => {
    form.resetFields()
    setCurrentStep(0)
  }, [form])

  const handleOk = useCallback(() => {
    setConfirmLoading(true)
  }, [])

  const handleCancel = useCallback(() => {
    resetValues()
    setOpen(false)
  }, [resetValues])

  const handleNextClick = useCallback(() => {
    form
      .validateFields()
      .then(() => setCurrentStep((currentStep) => currentStep + 1))
      .catch(() => {
        notificationService.notificationError({
          messageToDisplay: t('createWorkspaceModal.invalidFields'),
        })
      })
  }, [form])

  const handlePreviousClick = useCallback(() => {
    setCurrentStep((currentStep) => currentStep - 1)
  }, [])

  const handleSubmit = useCallback(() => {
    const selectedDataSourceId = form.getFieldValue('selectedDataSource')?.id
    
    if (!selectedDataSourceId) {
      notificationService.notificationError({
        messageToDisplay: t('createWorkspaceModal.errorCreateWorkspace'),
      })
      return
    }

    const workspace = {
      title: form.getFieldValue('title'),
      description: form.getFieldValue('description'),
      dataSource: selectedDataSourceId,
      dataStores: form.getFieldValue('selectedDataStores'),
      workspaceType: form.getFieldValue('type'),
      visibility: form.getFieldValue('visibility'),
      participants: form.getFieldValue('participants'),
      navigate,
    }
    workspaceService
      .createWorkspace(workspace)
      .then((response: WorkspaceDetails) => {
        notificationService.notificationSuccess({
          messageToDisplay: t('createWorkspaceModal.createWorkspaceSuccess'),
        })
        refreshWorkspaceList()
        resetValues()
        setOpen(false)
        navigate(`${routePaths.WORKSPACE_DETAILS}/${response.id}`)
      })
      .catch((error) => {
        console.error('axios fetch error', error)
        notificationService.notificationError({
          messageToDisplay: t('createWorkspaceModal.errorCreateWorkspace'),
        })
      })
    setConfirmLoading(false)
  }, [form, navigate, refreshWorkspaceList, resetValues])

  const steps: ReactNode[] = useMemo(
    () => [
      <CreateWorkspaceModalDefinitionStep form={form} />,
      <CreateWorkspaceModalTypeStep form={form} />,
      <CreateWorkspaceModalResourcesStep form={form} />,
      <CreateWorkspaceModalOutputStep form={form} />,
      <CreateWorkspaceModalPrivacyStep form={form} />,
      <CreateWorkspaceModalReviewStep form={form} />,
    ],
    [form]
  )

  const footers: ReactNode[] = useMemo(
    () => [
      <CreateWorkspaceModalFooter
        onNext={handleNextClick}
        onCancel={handleCancel}
      />,
      <CreateWorkspaceModalFooter
        onPrevious={handlePreviousClick}
        onNext={handleNextClick}
        onCancel={handleCancel}
      />,
      <CreateWorkspaceModalFooter
        onPrevious={handlePreviousClick}
        onNext={handleNextClick}
        onCancel={handleCancel}
      />,
      <CreateWorkspaceModalFooter
        onPrevious={handlePreviousClick}
        onNext={handleNextClick}
        onCancel={handleCancel}
      />,
      <CreateWorkspaceModalFooter
        onPrevious={handlePreviousClick}
        onNext={handleNextClick}
        onCancel={handleCancel}
      />,
      <CreateWorkspaceModalFooter
        onPrevious={handlePreviousClick}
        onSubmit={() => {
          // this will be changed in future mock
          handleSubmit()
        }}
        onCancel={handleCancel}
      />,
    ],
    [handleCancel, handleNextClick, handlePreviousClick, handleSubmit]
  )

  return (
    <>
      <Button
        icon={<ContainerOutlined />}
        type="primary"
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        {t('createWorkspaceModal.button')}
      </Button>
      <ModalBase
        key="main"
        title={
          <ModalStepsTitle
            title={t('createWorkspaceModal.title')}
            currentStep={currentStep}
            stepItems={stepItems}
          />
        }
        open={open}
        onOk={handleOk}
        confirmLoading={confirmLoading}
        onCancel={handleCancel}
        forceRender
        width={720}
        styles={{
          body: {
            maxHeight: 'calc(100vh - 400px)',
          },
        }}
        footer={footers[currentStep]}
      >
        <ModalContentContainer>
          <Form
            form={form}
            layout="vertical"
            initialValues={defaultValues}
            requiredMark={optionalRequiredMark}
          >
            {steps[currentStep]}
          </Form>
        </ModalContentContainer>
      </ModalBase>
    </>
  )
}

export default CreateWorkspaceModal
